import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Users, TrendingUp, BarChart3, Star, Info, History, Download } from 'lucide-react';
import { useOpsStore } from '../../store/ops.store';
import type { OpsScorecard } from '../../types/ops';

interface CardConfig {
  label: string;
  icon: React.ElementType;
  getValue: (s: OpsScorecard) => string;
  getColor: (s: OpsScorecard, isDark: boolean) => string;
  iconColor: string;
  tooltip: string | (() => string);
}

const cards: CardConfig[] = [
  {
    label: 'Tổng Check-in',
    icon: Users,
    getValue: (s) => s.totalCheckins.toLocaleString(),
    getColor: (_s, isDark) => isDark ? 'text-white' : 'text-gray-900',
    iconColor: 'text-blue-400',
    tooltip: 'Tổng lượt check-in\nĐếm số booking có trạng thái CheckedIn trong khoảng thời gian đã chọn.\nNếu bật "Exclude At Home" sẽ loại bỏ booking tập tại nhà.',
  },
  {
    label: 'Check-in kỳ trước',
    icon: History,
    getValue: (s) => s.previousCheckins.toLocaleString(),
    getColor: (_s, isDark) => isDark ? 'text-white' : 'text-gray-900',
    iconColor: 'text-slate-400',
    tooltip: 'Số lượt check-in kỳ trước\nKỳ trước có cùng độ dài với kỳ đang chọn, tính ngược từ ngày bắt đầu.',
  },
  {
    label: 'Check-in Growth',
    icon: TrendingUp,
    getValue: (s) => `${s.growthPercent >= 0 ? '+' : ''}${s.growthPercent.toFixed(1)}%`,
    getColor: (s) => (s.growthPercent >= 0 ? 'text-emerald-400' : 'text-red-400'),
    iconColor: 'text-emerald-400',
    tooltip: 'Tỷ lệ tăng trưởng check-in\nCông thức: (Check-in kỳ này - Check-in kỳ trước) / Check-in kỳ trước × 100%\nKỳ trước có cùng độ dài với kỳ đang chọn.',
  },
  {
    label: 'Occupancy TB',
    icon: BarChart3,
    getValue: (s) => `${(s.avgOccupancy * 100).toFixed(1)}%`,
    getColor: (_s, isDark) => isDark ? 'text-white' : 'text-gray-900',
    iconColor: 'text-amber-400',
    tooltip: () => {
      const formula = useOpsStore.getState().config?.capacityFormula;
      return formula === 'subtractBlock'
        ? 'Tỷ lệ lấp đầy trung bình\nCông thức: Trung bình cộng của (Check-in / Capacity) cho từng cặp Studio × Training Program.\n\nCapacity = CapacitySubtractBlock\n= (TimeSlots × MaxParticipants × Số ngày) − SUM(SlotTaken từ Booking Blocks)\n\nBooking Block = SessionType 50.\nmaxPax lấy từ DB (StudioTrainingPrograms).\nSlot tính tự động từ lịch hoạt động trong database.'
        : 'Tỷ lệ lấp đầy trung bình\nCông thức: Trung bình cộng của (Check-in / Capacity) cho từng cặp Studio × Training Program.\n\nCapacity = CapacityFull\n= TimeSlots × MaxParticipants × Số ngày\n\nKhông trừ slot bị khoá.\nmaxPax lấy từ DB (StudioTrainingPrograms).\nSlot tính tự động từ lịch hoạt động trong database.';
    },
  },
  {
    label: 'Rating TB',
    icon: Star,
    getValue: (s) => s.avgRating.toFixed(2),
    getColor: (s) => (s.avgRating >= 4.0 ? 'text-emerald-400' : s.avgRating >= 3.0 ? 'text-amber-400' : 'text-red-400'),
    iconColor: 'text-yellow-400',
    tooltip: 'Điểm đánh giá trung bình\nTính trung bình từ 3 loại rating: Coach, Studio, Training Program (1-5 sao).\nChỉ tính booking có ít nhất 1 rating.',
  },
];

function CardTooltip({ text, btnRef, isDark }: { text: string; btnRef: React.RefObject<HTMLButtonElement>; isDark: boolean }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const show = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const tooltipW = 272;
      let left = rect.left;
      if (left + tooltipW > window.innerWidth - 8) left = window.innerWidth - tooltipW - 8;
      if (left < 8) left = 8;
      setPos({ top: rect.top - 8, left });
    }
    setVisible(true);
  }, [btnRef]);

  return (
    <>
      <button
        ref={btnRef}
        className={`ml-auto transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
        onClick={() => { if (visible) setVisible(false); else show(); }}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {visible && createPortal(
        <div
          className={`fixed z-[9999] w-[272px] p-3 rounded-lg border shadow-xl text-xs whitespace-pre-line leading-relaxed ${isDark ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-700'}`}
          style={{ top: pos.top, left: pos.left, transform: 'translateY(-100%)' }}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
}

export default function ScorecardsRow({ isDark }: { isDark: boolean }) {
  const scorecards = useOpsStore((s) => s.scorecards);

  if (!scorecards) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={`backdrop-blur rounded-lg p-4 sm:p-5 animate-pulse h-24 sm:h-28 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
          />
        ))}
      </div>
    );
  }

  const exportCsv = () => {
    const header = cards.map(c => c.label);
    const values = cards.map(c => c.getValue(scorecards));
    const csvRows = [header.join(','), values.join(',')];
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'scorecards.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={exportCsv}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        const btnRef = useRef<HTMLButtonElement>(null);
        return (
          <div
            key={card.label}
            className={`backdrop-blur rounded-lg p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-2 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Icon className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 ${card.iconColor}`} />
              <span className={`text-xs sm:text-sm truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{card.label}</span>
              <CardTooltip text={typeof card.tooltip === 'function' ? card.tooltip() : card.tooltip} btnRef={btnRef} isDark={isDark} />
            </div>
            <span className={`text-2xl sm:text-3xl font-bold ${card.getColor(scorecards, isDark)}`}>
              {card.getValue(scorecards)}
            </span>
          </div>
        );
      })}
    </div>
    </div>
  );
}
